import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import LayoutShell from "@/components/ui/LayoutShell.jsx";
import Button from "@/components/ui/Button.jsx";
import Divider from "@/components/ui/Divider.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import AvatarThumb44 from "@/sot/primitives/AvatarThumb44.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { resolveDoctorAvatarSrc } from "@/lib/doctorsDirectory.js";
import {
  createProfessionalConnectionRequestRecoveryGuard,
  describeProfessionalConnectionRequestRecovery,
} from "@/lib/referrals/professionalConnectionEmailRecovery.mjs";
import { resolveCanonicalClinicianDisplayName } from "@/pages/shared/profileAddressedName.js";
import { formatClinicianSpecialtyLabel } from "@/utils/clinicianPresentation.js";
import messagingService from "@/services/messagingService.js";
import {
  decideProfessionalConnection,
  dispatchProfessionalConnectionEmail,
  disconnectProfessionalConnection,
  loadReferralNetworkDirectory,
  loadProfessionalConnections,
  requestProfessionalConnection,
} from "@/services/referralPackageService.js";

const clean = (value) => String(value || "").trim();

function traceRequestEmailRecovery(event, detail = {}) {
  if (!import.meta.env.DEV) return;
  console.info("[ReferralConnectionEmailRecovery]", { event, ...detail });
}

function clinicianContext(clinician) {
  const location = [clinician.facility_name, clinician.facility_city, clinician.facility_country]
    .filter(Boolean)
    .join(", ");
  const languages = Array.isArray(clinician.languages_spoken) && clinician.languages_spoken.length
    ? clinician.languages_spoken.join(", ")
    : "";
  return [
    formatClinicianSpecialtyLabel(clinician.specialty),
    location || "Consultancy not listed",
    languages,
  ].filter(Boolean).join(" · ");
}

function clinicianAddressedName(clinician) {
  return resolveCanonicalClinicianDisplayName(clinician, "Clinician");
}

function ClinicianRow({ clinician, trailing = null }) {
  return (
    <div className="flex min-h-14 items-center gap-3 py-3" data-afrimed-clinician-row="true">
      <AvatarThumb44
        size={40}
        shape="circle"
        role="doctor"
        src={resolveDoctorAvatarSrc(clinician)}
        alt=""
        className="shrink-0"
      />
      <div className="min-w-0 flex-1">
        <div className="text-body-large text-on-surface">{clinicianAddressedName(clinician)}</div>
        <div className="truncate text-body-small text-on-surface-var">{clinicianContext(clinician)}</div>
      </div>
      {trailing}
    </div>
  );
}

function ClinicianRows({ rows, renderTrailing, keyFor = (row) => row.connection_id || row.doctor_user_id }) {
  return (
    <div>
      {rows.map((row, index) => (
        <React.Fragment key={keyFor(row)}>
          <ClinicianRow clinician={row} trailing={renderTrailing(row)} />
          {index < rows.length - 1 ? <Divider insetStart={56} variant="outlineVariant" /> : null}
        </React.Fragment>
      ))}
    </div>
  );
}

function AcceptedConnectionRow({ clinician, busy = false, onMessage, onDisconnect }) {
  const actions = (
    <>
      <span className="inline-flex w-full min-w-0 items-center justify-center rounded-full bg-[var(--state-primary-12)] px-3 py-2 text-label-medium text-[rgb(var(--afrimed-primary))] sm:w-auto">
        Connected
      </span>
      <Button size="sm" variant="text" className="min-w-0 w-full sm:w-auto" disabled={busy} onClick={onMessage}>Message</Button>
      <Button size="sm" variant="text" className="min-w-0 w-full sm:w-auto" disabled={busy} onClick={onDisconnect}>Disconnect</Button>
    </>
  );

  return (
    <div className="py-3">
      <div className="flex min-w-0 items-center gap-3">
        <AvatarThumb44
          size={40}
          shape="circle"
          role="doctor"
          src={resolveDoctorAvatarSrc(clinician)}
          alt=""
          className="shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="text-body-large text-on-surface">{clinicianAddressedName(clinician)}</div>
          <div className="truncate text-body-small text-on-surface-var">{clinicianContext(clinician)}</div>
        </div>
        <div className="hidden shrink-0 items-center gap-2 sm:flex">{actions}</div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 sm:hidden">{actions}</div>
    </div>
  );
}

export default function DoctorReferralNetwork() {
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [connections, setConnections] = React.useState([]);
  const [connectionsLoading, setConnectionsLoading] = React.useState(true);
  const [directory, setDirectory] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [specialtyContext, setSpecialtyContext] = React.useState(() => clean(location.state?.referralSpecialty));
  const [directoryLoading, setDirectoryLoading] = React.useState(false);
  const [directoryHasMore, setDirectoryHasMore] = React.useState(false);
  const [busyId, setBusyId] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const pendingRequestEmailRecovery = React.useRef(createProfessionalConnectionRequestRecoveryGuard());

  const reload = React.useCallback(async () => {
    setConnectionsLoading(true);
    traceRequestEmailRecovery("connections_load_started", { actorUserId: clean(user?.id) });
    try {
      const network = await loadProfessionalConnections();
      setConnections(network);
      traceRequestEmailRecovery("connections_hydrated", {
        actorUserId: clean(user?.id),
        count: network.length,
        connections: network.map((connection) => ({
          connectionId: clean(connection.connection_id),
          status: clean(connection.connection_status),
          direction: clean(connection.connection_direction),
          peerUserId: clean(connection.doctor_user_id),
        })),
      });
    } catch (error) {
      console.error("[DoctorReferralNetwork] load failed", error);
      setNotice("Your referral network could not be loaded. Please try again.");
    } finally {
      setConnectionsLoading(false);
    }
  }, [user?.id]);

  React.useEffect(() => {
    traceRequestEmailRecovery("component_mounted", { actorUserId: clean(user?.id) });
    void reload();
  }, [reload, user?.id]);

  React.useEffect(() => {
    traceRequestEmailRecovery("recovery_effect_evaluated", {
      actorUserId: clean(user?.id),
      connectionsLoading,
      connectionCount: connections.length,
    });
    if (connectionsLoading) return;
    connections.forEach((connection) => {
      const recovery = describeProfessionalConnectionRequestRecovery(connection, user?.id);
      const guardBefore = pendingRequestEmailRecovery.current.snapshot(recovery.connectionId);
      traceRequestEmailRecovery("recovery_eligibility_evaluated", {
        ...recovery,
        requesterIsCurrentActor: recovery.direction === "outgoing",
        guardBefore,
      });
      const start = pendingRequestEmailRecovery.current.begin(recovery);
      if (!start.started) {
        traceRequestEmailRecovery("recovery_not_started", { ...recovery, reason: start.reason });
        return;
      }
      traceRequestEmailRecovery("dispatcher_entered", { ...recovery });
      void dispatchProfessionalConnectionEmail(recovery.connectionId, "requested")
        .then((result) => {
          const guardAfter = pendingRequestEmailRecovery.current.finish(recovery.connectionId, { success: result?.ok === true });
          traceRequestEmailRecovery("dispatcher_completed", {
            ...recovery,
            ok: result?.ok === true,
            status: clean(result?.status),
            dispatchId: clean(result?.dispatch_id),
            provider: clean(result?.provider),
            messageId: clean(result?.message_id),
            guardAfter,
          });
        })
        .catch((dispatchError) => {
          const guardAfter = pendingRequestEmailRecovery.current.finish(recovery.connectionId, { success: false });
          traceRequestEmailRecovery("dispatcher_failed", {
            ...recovery,
            errorCode: clean(dispatchError?.code),
            httpStatus: Number(dispatchError?.status || 0) || null,
            guardAfter,
          });
          console.warn("Professional connection request email could not be dispatched", dispatchError);
        });
    });
  }, [connections, connectionsLoading, user?.id]);

  React.useEffect(() => {
    const incomingSpecialty = clean(location.state?.referralSpecialty);
    if (incomingSpecialty) setSpecialtyContext(incomingSpecialty);
  }, [location.state]);

  React.useEffect(() => {
    const search = clean(query);
    const specialty = clean(specialtyContext);
    if (!search && !specialty) {
      setDirectory([]);
      setDirectoryHasMore(false);
      setDirectoryLoading(false);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setDirectoryLoading(true);
      try {
        const rows = await loadReferralNetworkDirectory({ search, specialty, limit: 20, offset: 0 });
        if (active) {
          setDirectory(rows);
          setDirectoryHasMore(rows.length === 20);
        }
      } catch (error) {
        console.error("[DoctorReferralNetwork] directory search failed", error);
        if (active) setNotice("Clinicians could not be found. Please try again.");
      } finally {
        if (active) setDirectoryLoading(false);
      }
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query, specialtyContext]);

  async function loadMoreDirectory() {
    const search = clean(query);
    const specialty = clean(specialtyContext);
    if (directoryLoading || (!search && !specialty)) return;
    setDirectoryLoading(true);
    try {
      const rows = await loadReferralNetworkDirectory({ search, specialty, limit: 20, offset: directory.length });
      setDirectory((current) => [...current, ...rows.filter((row) => !current.some((existing) => existing.doctor_user_id === row.doctor_user_id))]);
      setDirectoryHasMore(rows.length === 20);
    } catch (error) {
      console.error("[DoctorReferralNetwork] directory pagination failed", error);
      setNotice("More clinicians could not be found. Please try again.");
    } finally {
      setDirectoryLoading(false);
    }
  }

  const directoryById = React.useMemo(() => new Map(directory.map((row) => [row.doctor_user_id, row])), [directory]);
  const enrichedConnections = React.useMemo(() => connections.map((row) => ({ ...directoryById.get(row.doctor_user_id), ...row })), [connections, directoryById]);
  const accepted = enrichedConnections.filter((row) => row.connection_status === "accepted");
  const incoming = enrichedConnections.filter((row) => row.connection_status === "pending" && row.connection_direction === "incoming");
  const acceptedIds = new Set(accepted.map((row) => row.doctor_user_id));
  const pendingByDoctorId = new Map(enrichedConnections.filter((row) => row.connection_status === "pending").map((row) => [row.doctor_user_id, row]));
  const hasDiscoveryContext = Boolean(clean(query) || clean(specialtyContext));
  const discoverable = directory
    .filter((clinician) => clinician.doctor_user_id !== user?.id && !acceptedIds.has(clinician.doctor_user_id));

  async function run(id, task) {
    setBusyId(id);
    setNotice("");
    try { await task(); await reload(); }
    catch (error) {
      console.error("[DoctorReferralNetwork] connection action failed", error);
      setNotice("That professional connection could not be updated. Please try again.");
    }
    finally { setBusyId(""); }
  }

  async function requestConnection(clinician) {
    setBusyId(clinician.doctor_user_id);
    setNotice("");
    try {
      const result = await requestProfessionalConnection(clinician.doctor_user_id);
      pendingRequestEmailRecovery.current.finish(clean(result.connection_id), {
        success: result.request_email_dispatched === true,
      });
      setConnections((current) => [...current, {
        connection_id: result.connection_id,
        connection_status: "pending",
        connection_direction: "outgoing",
        doctor_user_id: clinician.doctor_user_id,
      }]);
      setNotice("Connection request sent.");
      void reload();
    } catch (error) {
      console.error("[DoctorReferralNetwork] connection request failed", error);
      setNotice("That professional connection could not be requested. Please try again.");
    } finally {
      setBusyId("");
    }
  }

  async function messageConnectedClinician(clinician) {
    const clinicianId = clean(clinician?.doctor_user_id);
    if (!clinicianId) return;
    setBusyId(clinicianId);
    setNotice("");
    try {
      const conversation = await messagingService.getOrCreateDirectConversation({
        otherUserId: clinicianId,
        title: clinicianAddressedName(clinician),
        type: "doctor_doctor",
      });
      nav(`/messages/${conversation.id}`, {
        state: { participantUserId: clinicianId, conversationType: "doctor_doctor" },
      });
    } catch (error) {
      console.error("[DoctorReferralNetwork] message connection failed", error);
      setNotice("That clinician conversation could not be opened. Please try again.");
    } finally {
      setBusyId("");
    }
  }

  return (
    <LayoutShell hideTabs flushTop freeScroll railClassName="w-full">
      <TopBarCenter title="Referral network" subtitle="Professional connections for governed referrals" onBack={() => nav(-1)} />
      <div className="w-full space-y-5 pb-24">
        <p className="text-body-medium text-on-surface-var">Professional connections make clinicians eligible for future referrals. They do not grant access to Patient records.</p>
        {notice ? <div className="text-body-medium text-[rgb(var(--afrimed-primary))]" role="status">{notice}</div> : null}
        {incoming.length ? <section className="space-y-2"><h2 className="text-title-medium text-on-surface">Connection requests</h2><ClinicianRows rows={incoming} renderTrailing={(row) => <div className="flex shrink-0 gap-2"><Button size="sm" variant="criticalTonal" disabled={busyId === row.connection_id} onClick={() => run(row.connection_id, () => decideProfessionalConnection(row.connection_id, "declined"))}>Decline</Button><Button size="sm" variant="tonal" loading={busyId === row.connection_id} onClick={() => run(row.connection_id, () => decideProfessionalConnection(row.connection_id, "accepted"))}>Accept</Button></div>} /></section> : null}
        <section className="space-y-2"><h2 className="text-title-medium text-on-surface">Referral network</h2>{accepted.length ? <div>{accepted.map((row, index) => <React.Fragment key={row.connection_id || row.doctor_user_id}><AcceptedConnectionRow clinician={row} busy={Boolean(busyId)} onMessage={() => void messageConnectedClinician(row)} onDisconnect={() => run(row.connection_id, () => disconnectProfessionalConnection(row.connection_id))} />{index < accepted.length - 1 ? <Divider insetStart={56} variant="outlineVariant" /> : null}</React.Fragment>)}</div> : <div className="text-body-small text-on-surface-var">No accepted professional connections yet.</div>}</section>
        <section className="space-y-3"><div><h2 className="text-title-medium text-on-surface">Find specialists</h2><p className="text-body-small text-on-surface-var">Directory discovery is separate from referral eligibility. Search to find a clinician and send a professional connection request.</p></div>{specialtyContext ? <div className="flex items-center justify-between gap-3 text-body-small text-on-surface-var"><span>Specialty: {specialtyContext}</span><Button size="sm" variant="text" onClick={() => setSpecialtyContext("")}>Clear</Button></div> : null}<div className="space-y-1"><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search clinicians" className="w-full rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 text-body-large text-on-surface outline-none" /><p className="text-body-small text-on-surface-var">Search by name, specialty or practice.</p></div>{directoryLoading && directory.length === 0 ? <div className="text-body-small text-on-surface-var">Searching clinicians…</div> : null}{hasDiscoveryContext && !directoryLoading && discoverable.length === 0 ? <div className="text-body-small text-on-surface-var">No globally discoverable clinicians match this search.</div> : null}{hasDiscoveryContext && discoverable.length ? <><ClinicianRows rows={discoverable} renderTrailing={(clinician) => { const pending = pendingByDoctorId.get(clinician.doctor_user_id); return pending ? <span className="shrink-0 rounded-full bg-[var(--state-primary-12)] px-3 py-2 text-label-medium text-[rgb(var(--afrimed-primary))]">Pending</span> : <Button size="sm" variant="tonal" loading={busyId === clinician.doctor_user_id} onClick={() => requestConnection(clinician)}>Connect</Button>; }} />{directoryHasMore ? <Button size="sm" variant="text" loading={directoryLoading} onClick={() => void loadMoreDirectory()}>Load more</Button> : null}</> : null}{!hasDiscoveryContext ? <div className="text-body-small text-on-surface-var">Search the Global clinician directory to grow your Referral Pool.</div> : null}</section>
      </div>
    </LayoutShell>
  );
}
